import type { Point, ShapeStyle, Size } from '../state/types';
import { Rectangle } from '../shapes/Rectangle';
import { Ellipse } from '../shapes/Ellipse';
import { Diamond } from '../shapes/Diamond';
import { Cylinder } from '../shapes/Cylinder';
import { Triangle } from '../shapes/Triangle';
import type { Shape } from '../shapes/Shape';
import { RECOGNITION_CONFIG } from '../recognition/config';
import { findCorners, getBoundingBox, isClosedShape, simplifyPoints } from '../recognition/geometry';
import { recognizeConnector } from '../recognition/recognizers/connector';
import { recognizeEllipse } from '../recognition/recognizers/ellipse';
import {
  createRectangleResult,
  recognizeRectangle,
  type RectangleRecognitionInput,
} from '../recognition/recognizers/rectangle';
import type { RecognitionResult } from '../recognition/types';

export class ShapeRecognizer {
  recognize(points: Point[], style?: Partial<ShapeStyle>): RecognitionResult {
    if (points.length < RECOGNITION_CONFIG.minPoints) {
      return { type: 'none', confidence: 0 };
    }

    const bounds = getBoundingBox(points);
    const simplified = simplifyPoints(points, RECOGNITION_CONFIG.simplifyTolerance);

    const aspectRatio = bounds.width / bounds.height;
    if (
      aspectRatio > RECOGNITION_CONFIG.lineAspectRatioThreshold ||
      aspectRatio < 1 / RECOGNITION_CONFIG.lineAspectRatioThreshold
    ) {
      const lineResult = recognizeConnector(points);
      if (lineResult.confidence > RECOGNITION_CONFIG.confidenceThreshold) {
        return lineResult;
      }
    }

    const closed = isClosedShape(points, bounds);

    if (!closed) {
      const lineResult = recognizeConnector(points);
      if (lineResult.confidence > RECOGNITION_CONFIG.nonClosedLineConfidenceThreshold) {
        return lineResult;
      }
    }

    const ellipseResult = recognizeEllipse(points, bounds, style);
    const corners = findCorners(simplified, bounds);
    const rectangleInput: RectangleRecognitionInput = { simplified, bounds, corners };
    if (style) {
      rectangleInput.style = style;
    }
    const rectangleResult = recognizeRectangle(rectangleInput);

    if (corners.length <= 2) {
      if (ellipseResult.confidence > RECOGNITION_CONFIG.ellipseConfidenceThreshold) {
        return ellipseResult;
      }
    } else if (corners.length >= 3) {
      if (rectangleResult.confidence > RECOGNITION_CONFIG.rectangleConfidenceThreshold) {
        return rectangleResult;
      }
    }

    if (
      ellipseResult.confidence >= rectangleResult.confidence &&
      ellipseResult.confidence > RECOGNITION_CONFIG.ellipseConfidenceThreshold
    ) {
      return ellipseResult;
    }
    if (rectangleResult.confidence > RECOGNITION_CONFIG.rectangleConfidenceThreshold) {
      return rectangleResult;
    }

    if (closed) {
      return createRectangleResult(
        bounds,
        style,
        RECOGNITION_CONFIG.rectangleClosedFallbackConfidence
      );
    }

    return { type: 'none', confidence: 0 };
  }

  createShapeAtPosition(
    type: 'rectangle' | 'ellipse' | 'diamond' | 'cylinder' | 'triangle',
    position: Point,
    size: Size = { width: 120, height: 80 },
    style?: Partial<ShapeStyle>
  ): Shape {
    const centeredPosition = {
      x: position.x - size.width / 2,
      y: position.y - size.height / 2,
    };

    switch (type) {
      case 'rectangle':
        return new Rectangle(centeredPosition, size, style);
      case 'ellipse':
        return new Ellipse(centeredPosition, size, style);
      case 'diamond':
        return new Diamond(centeredPosition, size, style);
      case 'cylinder':
        return new Cylinder(centeredPosition, { ...size, height: size.height * 1.2 }, style);
      case 'triangle':
        return new Triangle(centeredPosition, size, style);
    }
  }
}
